import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TabsProvider } from "./contexts/TabsContext";
import TabPagesRouter from "./components/TabPagesRouter";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import SpaceViewPage from "./pages/SpaceViewPage";
import CalendarPage from "./pages/CalendarPage";
import AdminPage from "./pages/AdminPage";
import BankingPage from "./pages/BankingPage";
import TodosPage from "./pages/TodosPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import EmployeesPage from "./pages/EmployeesPage";
import VacationsPage from "./pages/VacationsPage";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import SuperchatPage from "./pages/SuperchatPage";
import TimetrackPage from "./pages/TimetrackPage";
import ShiftPlannerPage from "./pages/ShiftPlannerPage";
import CasesPage from "./pages/CasesPage";
import DrivePage from "./pages/DrivePage";
import WhatsAppPage from "./pages/WhatsAppPage";
import ClaudePage from "./pages/ClaudePage";
import NotesPage from "./pages/NotesPage";
import MailPage from "./pages/MailPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ImprintPage from "./pages/ImprintPage";
import PrivacyPage from "./pages/PrivacyPage";
import CookieBanner from "./components/CookieBanner";
import AppLayout from "./layout/AppLayout";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return <AppLayout>{children}</AppLayout>;
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
        );
    }
    if (user) return <Navigate to="/" replace />;
    return children;
}

function PrivOnly({ children }) {
    const { user } = useAuth();
    if (user?.role !== "admin" && user?.role !== "master") return <Navigate to="/" replace />;
    return children;
}

/** All protected pages — TabPagesRouter renders each one as a separate hidden/visible div. */
const PROTECTED_ROUTES = [
    { pattern: "/",                element: <DashboardPage /> },
    { pattern: "/space/:spaceId",  element: <SpaceViewPage /> },
    { pattern: "/calendar",        element: <CalendarPage /> },
    { pattern: "/banking",         element: <BankingPage /> },
    { pattern: "/todos",           element: <TodosPage /> },
    { pattern: "/announcements",   element: <AnnouncementsPage /> },
    { pattern: "/employees",       element: <EmployeesPage /> },
    { pattern: "/vacations",       element: <VacationsPage /> },
    { pattern: "/chat",            element: <ChatPage /> },
    { pattern: "/settings",        element: <SettingsPage /> },
    { pattern: "/superchat",       element: <SuperchatPage /> },
    { pattern: "/whatsapp",        element: <PrivOnly><WhatsAppPage /></PrivOnly> },
    { pattern: "/claude",          element: <ClaudePage /> },
    { pattern: "/mail",            element: <MailPage /> },
    { pattern: "/notes",           element: <NotesPage /> },
    { pattern: "/zeiterfassung",   element: <TimetrackPage /> },
    { pattern: "/schichtplan",     element: <ShiftPlannerPage /> },
    { pattern: "/cases",           element: <CasesPage /> },
    { pattern: "/drive",           element: <DrivePage /> },
    { pattern: "/admin",           element: <PrivOnly><AdminPage /></PrivOnly> },
];

function ProtectedShell() {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return (
        <AppLayout>
            <TabPagesRouter routeMap={PROTECTED_ROUTES} />
        </AppLayout>
    );
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <TabsProvider>
                        <Routes>
                            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                            <Route path="/invite/:token" element={<AcceptInvitePage />} />
                            <Route path="/impressum" element={<ImprintPage />} />
                            <Route path="/datenschutz" element={<PrivacyPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/*" element={<ProtectedShell />} />
                        </Routes>
                        <CookieBanner />
                    </TabsProvider>
                </BrowserRouter>
            </AuthProvider>
        </div>
    );
}
